import { useState } from "react";
import { ProblemError } from "@csa/web-foundation";
import { useT } from "../../i18n/index";
import { Icon } from "../../ui/icons";
import {
  useAssignableAdvisors,
  useReassignLead,
  type AssignableAdvisor,
} from "./use-prepare-offers";

export interface ReassignDialogProps {
  leadId: string;
  onClose: () => void;
  /** Fired once the reassignment commits — carries the chosen advisor so the
   *  caller can show the new owner + a success toast. */
  onReassigned: (advisor: AssignableAdvisor) => void;
}

/** "Danışmana ata / Devret" — a small governed dialog: lists the org's ACTIVE
 *  advisors (GET /v1/leads/assignable-advisors), and on confirm reassigns the
 *  lead (POST /v1/leads/:leadId/assign { ownerStaffId }). ProblemJson errors are
 *  surfaced inline; the ui-kit overlay/dialog primitives carry the styling. */
export function ReassignDialog({ leadId, onClose, onReassigned }: ReassignDialogProps) {
  const { t } = useT();
  const advisors = useAssignableAdvisors();
  const reassign = useReassignLead(leadId);
  const [selectedId, setSelectedId] = useState<string>("");

  const options = advisors.data ?? [];
  const problem = reassign.error instanceof ProblemError ? reassign.error.problem : null;
  const canConfirm = selectedId !== "" && !reassign.isPending;

  const confirm = () => {
    const advisor = options.find((a) => a.staffId === selectedId);
    if (!advisor) return;
    reassign.mutate(advisor.staffId, {
      onSuccess: () => {
        onReassigned(advisor);
        onClose();
      },
    });
  };

  return (
    <div
      className="k-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={t("po.reassign.title")}
      data-testid="reassign-dialog"
    >
      <div className="k-dialog">
        <div className="k-dialog__icon k-dialog__icon--primary">
          <Icon name="member" size={22} />
        </div>
        <h2 className="k-dialog__title">{t("po.reassign.title")}</h2>
        <p className="k-dialog__desc">{t("po.reassign.desc")}</p>

        <label className="k-field" style={{ marginBottom: 20 }}>
          <span className="k-field__label">{t("po.reassign.advisor")}</span>
          {advisors.isLoading ? (
            <span className="k-field__hint" data-testid="reassign-loading">
              {t("po.reassign.loading")}
            </span>
          ) : advisors.isError ? (
            <span role="alert" className="k-field__error" data-testid="reassign-list-error">
              <Icon name="alert-triangle" size={14} /> {t("po.reassign.listError")}
            </span>
          ) : options.length === 0 ? (
            <span className="k-field__hint" data-testid="reassign-empty">
              {t("po.reassign.empty")}
            </span>
          ) : (
            <span className="k-select">
              <select
                className="k-select__native"
                aria-label={t("po.reassign.advisor")}
                value={selectedId}
                disabled={reassign.isPending}
                onChange={(e) => setSelectedId(e.target.value)}
              >
                <option value="">{t("po.reassign.choose")}</option>
                {options.map((a) => (
                  <option key={a.staffId} value={a.staffId}>
                    {a.displayName}
                  </option>
                ))}
              </select>
              <span className="k-select__chevron">
                <Icon name="chevron-down" size={16} />
              </span>
            </span>
          )}
        </label>

        {problem && (
          <p role="alert" className="k-field__error" data-testid="reassign-error">
            <Icon name="alert-triangle" size={14} /> {problem.detail}
          </p>
        )}

        <div className="k-dialog__actions">
          <button
            type="button"
            className="k-btn k-btn--ghost k-btn--sm"
            disabled={reassign.isPending}
            onClick={onClose}
          >
            {t("po.reassign.cancel")}
          </button>
          <button
            type="button"
            className="k-btn k-btn--primary k-btn--sm"
            data-testid="reassign-confirm"
            disabled={!canConfirm}
            onClick={confirm}
          >
            {reassign.isPending ? t("po.reassign.saving") : t("po.reassign.confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}
